// services/database_service.js
import { openDB } from 'idb'
import MediaStorageService from "../services/media_storage_service"

export default class PatrolDatabaseService {
  constructor() {
    this.DB_NAME = 'patrols'
    this.DB_VERSION = 2
  }

  async initDB() {
    return await openDB(this.DB_NAME, this.DB_VERSION, {
      upgrade: (db) => {
        // Using arrow function to preserve 'this' context
        if (!db.objectStoreNames.contains('patrols')) {
          db.createObjectStore('patrols', { keyPath: 'road_id' })
        }
        if (!db.objectStoreNames.contains('checkpoints')) {
          const checkpointStore = db.createObjectStore('checkpoints', { keyPath: 'road_id' })
          checkpointStore.createIndex('by_road', 'road_id')
        }
        if (!db.objectStoreNames.contains('completion_times')) {
          db.createObjectStore('completion_times', { 
            keyPath: ['road_id', 'checkpoint_id'] 
          })
        }
        if (!db.objectStoreNames.contains('gps_coordinates')) {
          db.createObjectStore('gps_coordinates', { 
            keyPath: 'id',
            autoIncrement: true 
          })
        }
      }
    })
  }
  

  async storePatrolData(data) {
    const db = await this.initDB()
    try {
      const tx = db.transaction(['patrols', 'checkpoints'], 'readwrite')
      
      await tx.objectStore('patrols').put({
        name: data.name,
        description: data.description,
        total_time: data.total_time,
        end_time: data.end_time,
        start_time: null,
        road_id: data.id
      })

      if (data.checkpoints?.length) {
        const store = tx.objectStore('checkpoints')
        await Promise.all(data.checkpoints.map(checkpoint => 
          store.put({ ...checkpoint, road_id: data.id })
        ))
      }

      await tx.done
    } finally {
      db.close()
    }
  }

  async storeGpsCoordinates(coordinates) {
    const db = await this.initDB()
    try {
      const tx = db.transaction('gps_coordinates', 'readwrite')
      await tx.store.add(coordinates)
      await tx.done
    } finally {
      db.close()
    }
  }

  async getCompletionTimes(roadId) {
    const db = await this.initDB()
    try {
      const tx = db.transaction(['completion_times'], 'readonly')
      const times = await tx.store.getAll()
      
      return times
        .filter(time => time.road_id === roadId)
        .map(({ road_id, ...rest }) => rest)
    } finally {
      db.close()
    }
  }

  async deleteDB() {
    let db = null;
    try {
      db = await this.initDB();
      
      // Get all store names
      const storeNames = db.objectStoreNames;
      
      if (storeNames.length > 0) {
        // Create a transaction that includes all stores
        const tx = db.transaction(Array.from(storeNames), 'readwrite');
        
        // Clear each store
        const clearPromises = Array.from(storeNames).map(storeName => {
          console.log(`Clearing store: ${storeName}`);
          return tx.objectStore(storeName).clear();
        });
    
        // Wait for all stores to be cleared
        await Promise.all(clearPromises);
        
        // Wait for the transaction to complete
        await tx.done;
      }
      
      console.log('All tables cleared successfully');
    } catch (error) {
      console.error('Error clearing tables:', error);
      throw error;
    } finally {
      if (db) {
        db.close();
        console.log('Database connection closed');
      }
    }
  }

  async storePatrolStartTime(roadId) {
    const db = await this.initDB()
    try {
      const tx = db.transaction('patrols', 'readwrite')
      const store = tx.objectStore('patrols')
      
      // Get the existing patrol
      const patrol = await store.get(roadId)
      if (!patrol) {
        throw new Error('Patrol not found')
      }
  
      // Update the start_time
      patrol.start_time = new Date().toISOString()
      await store.put(patrol)
      await tx.done
    } finally {
      db.close()
    }
  }
  
  async storeCompletionTime(roadId, checkpointId) {
    const db = await this.initDB()
    try {
      const tx = db.transaction('completion_times', 'readwrite')
      await tx.store.put({
        road_id: roadId,
        checkpoint_id: checkpointId,
        completed_at: new Date().toISOString()
      })
      await tx.done
    } finally {
      db.close()
    }
  }
  
  async getPatrol(roadId) {
    const db = await this.initDB()
    try {
      const tx = db.transaction('patrols', 'readonly')
      const store = tx.objectStore('patrols')
      
      // Get all patrols and find the one matching the road_id
      const allPatrols = await store.getAll()
      return allPatrols.find(patrol => patrol.road_id === roadId)
    } finally {
      db.close()
    }
  }
  
  async getLastCompletionTime(roadId) {
    const db = await this.initDB()
    try {
      const tx = db.transaction('completion_times', 'readonly')
      const store = tx.objectStore('completion_times')
      
      // Get all completion times for this road
      const allCompletionTimes = await store.getAll()
      const roadCompletionTimes = allCompletionTimes.filter(ct => ct.road_id === roadId)
      
      // Sort by completion_time in descending order and get the first one
      if (roadCompletionTimes.length > 0) {
        return roadCompletionTimes
          .sort((a, b) => new Date(b.completed_at) - new Date(a.completed_at))[0]
          .completed_at
      }
      
      return null
    } finally {
      db.close()
    }
  }
  
  async getGpsCoordinates(roadId) {
    const db = await this.initDB()
    try {
      const tx = db.transaction('gps_coordinates', 'readonly')
      const store = tx.objectStore('gps_coordinates')
      
      // Get all coordinates and filter by road_id
      const allCoordinates = await store.getAll()
      console.log(allCoordinates)
      return allCoordinates.filter(coord => coord.road_id === roadId)
        .map(coord => ({
          latitude: coord.latitude,
          longitude: coord.longitude,
          accuracy: coord.accuracy,
          altitude: coord.altitude,
          altitude_accuracy: coord.altitude_accuracy,
          heading: coord.heading,
          speed: coord.speed,
          timestamp: coord.timestamp,
          checkpoint_id: coord.checkpoint_id
        }))
    } finally {
      db.close()
    }
  }
  
  async getMediaData(roadId) {
    const mediaStorage = new MediaStorageService()
    await mediaStorage.initDB()
    return await mediaStorage.getMediaForGroup(roadId)
  }
}
